<template>
  <div
    v-if="activity"
    class="goal-detail mt-3">
    <ul class="list-unstyled">
      <li
        class="goal-detail__item mb-3"
        data-testid="activity-item">
        <div
          class="activity-result-header"
          data-testid="activity-header"
          @click="onClickCard">
          <div
            v-if="activity.isDraft"
            class="activity-result-header__draft"
            data-testid="activity-draft-label">
            下書き
          </div>
          <img
            v-if="attachmentOption && activity.file"
            src="@/assets/images/file-icon.svg"
            class="activity-result-header__file-icon">
          <h3 :class="activityItemClass">
            <TemplateColorChip :color="activity.templateColor" />{{ activity.name }}
          </h3>
          <div class="goal-detail__box">
            <div
              class="goal-detail__has-comment text-center">
              <img
                v-if="activity.comments && activity.comments.length > 0"
                src="@/assets/images/icon_comment.svg"
                class="goal-detail__comment-icon mb-1">
            </div>
            <div class="goal-detail__date">
              {{ activity.workAt | prettyDate }}
            </div>
          </div>
        </div>
        <transition name="list-menu">
          <div
            v-if="isShowDetail"
            class="goal-detail__body">
            <div class="goal-detail__record record p-2">
              <div class="d-flex justify-content-between">
                <h4 class="record__heading mb-4">
                  活動記録<span class="record__date ml-2">{{ activity.updatedAt | prettyDateTime }}</span>
                </h4>
                <div
                  v-if="isLoginUser && !isPeriodOver"
                  class="record__edit">
                  <button
                    class="record__edit-btn btn btn--circle btn-l-blue"
                    @click="onClickEditActivityBtn">
                    <span class="record__edit-icon material-icons">
                      edit
                    </span>
                  </button>
                </div>
              </div>
              <!-- eslint-disable -->
              <div
                class="record__text"
                v-html="sanitizedLink(activity.detail)" />
              <!-- eslint-enable -->
              <file-viewer
                v-if="attachmentOption && activity.file"
                :file="activity.file"
                :filename="activity.filename"
                :thumbnail="activity.thumbnail"
                class="mt-3" />
            </div>
            <div
              v-if="isShowConditionPanel"
              class="goal-detail__condition">
              <ConditionPanel
                :activity="activity">
                <template
                  v-if="hasCondition"  
                  #header>
                  <h4 class="condition__heading mb-2">
                    コンディション
                  </h4>
                </template>
              </ConditionPanel>
            </div>
            <div
              v-if="!isPeriodOver && !activity.isDraft"
              class="goal-detail__footer py-3 px-2 d-flex justify-content-end"
              @click.stop>
              <TextButton
                data-testid="like-button"
                class="d-flex align-items-center mr-1"
                :class="{'like-btn': isLike}"
                :disabled="isParentMember"
                @click="onClickLike">
                <span
                  class="material-icons mr-1"
                  style="font-size: 1rem;">
                  thumb_up
                </span>
                いいね
              </TextButton>
              <TextButton
                :class="{'like-btn': isLike}"
                @click="onClickLikeCount">
                +{{ likeCount }}
              </TextButton>
            </div>
            <slot name="comment" />
          </div>
        </transition>
      </li>
    </ul>
  </div>
</template>

<script>
import sanitizedLinkMixin from '@/components/mixin/sanitizedLink'
import FileViewer from '@/components/parts/FileViewer'
import ConditionPanel from '@/components/parts/ConditionPanel.vue'
import TextButton from '@/components/parts/TextButton'
import TemplateColorChip from '@/components/parts/TemplateColorChip.vue'
import { mapState } from 'vuex'
export default {
  components: {
    FileViewer,
    ConditionPanel,
    TextButton,
    TemplateColorChip,
  },
  mixins: [sanitizedLinkMixin],
  props: {
    activity: {
      type: Object,
      required: true
    },
    goalId: {
      type: String,
      required: true
    },
    memberId: {
      type: String,
      required: true
    },
    loginUserId: {
      type: String,
      required: true
    },
    isShowDetail: {
      type: Boolean,
      required: true
    },
    isPeriodOver: {
      type: Boolean,
      required: true
    },
    isParentMember: {
      type: Boolean,
      required: true
    },
    attachmentOption: {
      type: Boolean,
      required: true
    },
    conditionSetting: {
      type: Object,
      required: true
    },
    bodyPainSetting: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showActivityDetail: false,
      isLike: false,
      isProtecting: false,
      likeUsers: [],
      likeCount: 0,
      conditionNames: {
        fatigueLevel: '疲労度',
        satisfactionLevel: '充実度',
        motivationLevel: 'モチベ',
        healthStatus: '体調',
        bodyTemperature: '体温',
        bodyWeight: '体重',
        bodyHeight: '身長',
        sleepHours: '睡眠',
        studyHours: '学習',
        menstrualRecord: '生理',
        schoolLife: '学校'
      }
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    activityItemClass() {
      if (this.activity.star) {
        return 'goal-detail__heading--marked'
      }
      return 'goal-detail__heading'
    },
    isLoginUser() {
      return this.loginUserId === this.memberId
    },
    hasCondition() {
      return !(this.conditionSetting === null || this.activity.condition === null)
    },
    hasBodyPain() {
      return this.bodyPainSetting !== null && this.activity.bodyPain !== null && this.bodyPainSetting.isRecordBodyPain && this.activity.bodyPain.text !== ''
    },
    isShowConditionPanel() {
      return this.hasCondition || this.hasBodyPain
    }
  },
  created() {
    if (this.activity.isDraft) return
    this.isLike = this.activity.liked
    this.likeCount = this.activity.likeCount
  },
  methods: {
    onClickEditActivityBtn() {
      this.$router.push(
        {
          path: `${this.memberId}/goal/${this.goalId}/activity_result_edit/${this.activity.id}`,
          query: {isDraft: this.activity.isDraft}
        })
    },
    onClickCard() {
      this.$emit('showActivityDetail', this.goalId, this.activity.id)
    },
    onClickLike() {
      // いいねの連打によるサーバー負荷軽減のため、押してから500msの間は処理が走らないようにする
      if (this.isProtecting) return
      if (this.isLike) {
        this.likeCount = this.likeCount - 1
        this.$emit('dislike', this.activity.id)
      } else {
        this.likeCount = this.likeCount + 1
        this.$emit('like', this.activity.id)
      }
      this.isLike = !this.isLike
      // protect開始
      this.isProtecting = true
      // 500ms後にprotect解除
      window.setTimeout(this.clearProtect, 500)
    },
    clearProtect() {
      this.isProtecting = false
    },
    onClickLikeCount() {
      this.$emit('like-count', this.activity.id)
    },
  }
}
</script>

<style lang="scss" scoped>
.goal-detail{
  cursor: auto;

  &__edit{
    color: $grey01;
    text-decoration: none;
    display: block;
    text-align: right;
    font-weight: bold;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
  }
  &__icon{
    display: inline-block;
    vertical-align: middle;
  }
  &__item{
    border: solid 1px $grey10;
    border-radius: 8px;
  }
  &__heading,
  &__heading--marked{
    font-size: $fs-nm;
    font-weight: bold;
    position: relative;
    padding-left: 16px;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
    &:before{
      content: "";
      position: absolute;
      background: $grey16;
      top: 4px;
      left: 0;
      height: 12px;
      width: 12px;
      border-radius: 50%;
    }
  }
  &__heading--marked:before{
    background: $pink;
  }
  &__footer {
    border-bottom: solid 1px $grey10;
  }
  &__box{
    text-align: right;
  }
  &__count{
    font-size: $fs-m;
    color: $l-blue;
    font-weight: bold;
  }
  &__date{
    font-size: $fs-s;
  }
  &__body{
    border-top: solid 1px $grey10;
  }

  &__has-comment {
    height: 28px;
  }

  &__comment-icon {
    width: 16px;
    height: 16px;
  }
}

.activity-result-header {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  cursor: pointer;
  &__file-icon {
    position: absolute;
    top: -10px;
    right: -8px;
  }
  &__draft {
    position: absolute;
    background-color: $grey09;
    color: $white;
    padding: 1px 8px;
    top: -10px;
    right: 20px;
    font-size: 12px;
  }
}
.record{
  &__heading{
    font-size: $fs-nm;
    font-weight: bold;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
  }
  &__date{
    color: $grey09;
    font-size: $fs-m;
    @media screen and (max-width:320px) {
      font-size: $fs-s;
    }
  }
  &__edit{
    position: relative;
  }
  &__edit-btn{
    box-shadow: 1px 2px 6px $grey05;
  }
  &__edit-icon{
    position: absolute;
    left: 0;
    top: 0;
    line-height: 34px;
    width: 34px;
  }
  &__text{
    color: $grey17;
    white-space:pre-wrap;
    word-wrap:break-word;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
  }
}
.list-menu-enter-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.list-menu-leave-active {
  transition: all 0.1s;
}
.list-menu-enter, .list-menu-leave-to {
  opacity: 0;
  transform: translateY(-15px);
}

.goal-detail__condition {
  padding: 15px 10px 0;
  margin-top: 15px;
  border-top: solid 1px $grey10;

  .condition__heading{
    font-size: $fs-nm;
    font-weight: bold;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
  }

  .conditions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .condition {
    text-align: center;
    width: calc(100% / 4 - 10px * (3 / 4));
    font-size: $fs-m;
    .value {
      background: $text-background;
      padding: 5px 0;
    }
  }

  .body-pain {
    &__heading {
      font-size: $fs-m;
      font-weight: bold;
      padding-bottom: 5px;
    }
    &__text {
      font-size: $fs-m;
      background: $text-background;
      padding: 5px 10px;
      width: 100%;
    }
  }
}
.like-btn {
  color: $primary;
}
</style>